import BaseUploadController from './base_upload_controller'
import NotificationController from './notification_controller'

export default class extends BaseUploadController {
  static targets = ['input', 'progressContainer', 'submitButton', 'errorContainer']
  static values = { 
    url: String,
    maxFileSize: Number,
    acceptedTypes: Array
  }

  connect() {
    super.connect() // Call parent connect
    this.uploads = new Map()
    this.completed = new Map()
    this.errors = new Map()
    this.setupFileInputListener()
    this.setupFormSubmitInterceptor()
    this.checkSubmitButtonState()
  }

  setupFileInputListener() {
    this.inputTargets.forEach(input => {
      input.addEventListener('change', (event) => this.handleFileSelection(event))
    })
  }

  setupFormSubmitInterceptor() {
    // Find the form element
    const form = this.element.querySelector('form') || this.element.closest('form')
    if (!form) return

    // Intercept form submission
    form.addEventListener('submit', (event) => {
      // Check if we have pending uploads
      const hasActiveUploads = this.uploads.size > this.completed.size + this.errors.size
      
      if (hasActiveUploads) {
        event.preventDefault()
        NotificationController.alert('Veuillez attendre la fin des uploads avant de soumettre le formulaire.')
        return false
      }

      // Check if we have any file inputs with files that haven't been uploaded
      const fileInputs = form.querySelectorAll('input[type="file"]')
      let hasUnuploadedFiles = false
      
      fileInputs.forEach(input => {
        if (input.files && input.files.length > 0 && input.style.display !== 'none') {
          hasUnuploadedFiles = true
          // Trigger upload for these files
          this.handleFileSelection({ target: input })
        }
      })

      if (hasUnuploadedFiles) {
        event.preventDefault()
        NotificationController.info(
          'Upload en cours',
          'Upload des fichiers en cours. Le formulaire sera soumis automatiquement une fois terminé.',
          { timer: 3000 }
        )
        
        // Set up auto-submit when uploads complete
        this.autoSubmitOnComplete = true
        return false
      }
    })
  }

  handleFileSelection(event) {
    const input = event.target
    const files = Array.from(input.files)
    
    if (files.length === 0) return

    // Clear previous errors for this input
    this.clearErrors(input)
    
    // Validate files
    const validationResult = this.validateFiles(files, input)
    if (!validationResult.valid) {
      this.showErrors(input, validationResult.errors)
      input.value = '' // Clear invalid files
      return
    }

    // Start uploads
    this.startUploads(files, input)
  }

  validateFiles(files, input) {
    const errors = []
    const maxSize = this.maxFileSizeValue || 50 * 1024 * 1024 // 50MB default
    const acceptedTypes = this.acceptedTypesValue || []

    files.forEach((file, index) => {
      // Check file size
      if (file.size > maxSize) {
        errors.push(`${file.name}: Fichier trop volumineux (${this.formatFileSize(file.size)}). Taille maximum: ${this.formatFileSize(maxSize)}`)
      }

      // Check file type
      if (acceptedTypes.length > 0 && !acceptedTypes.some(type => file.type.includes(type))) {
        errors.push(`${file.name}: Type de fichier non supporté. Types acceptés: ${acceptedTypes.join(', ')}`)
      }
    })

    return {
      valid: errors.length === 0,
      errors: errors
    }
  }

  startUploads(files, input) {
    this.disableSubmitButton()
    this.showProgressContainer()

    const inputName = input.name || 'files'
    const stemIndex = this.extractStemIndex(input)
    
    files.forEach((file, index) => {
      const uploadId = `${stemIndex}_${index}_${Date.now()}`
      this.createProgressElement(uploadId, file.name)
      this.startDirectUpload(file, uploadId, input, stemIndex)
    })
  }

  // Method moved to base class

  async startDirectUpload(file, uploadId, input, stemIndex) {
    this.uploads.set(uploadId, { file, input, stemIndex })
    
    try {
      // Use the shared DirectUpload method from base class
      const blob = await this.performDirectUpload(file, (progress) => {
        this.updateProgress(uploadId, progress)
      })
      
      this.handleUploadSuccess(uploadId, blob)
    } catch (error) {
      this.handleUploadError(uploadId, error)
    }
    
    this.checkAllUploadsComplete()
  }

  // DirectUpload delegate methods - not used directly anymore as we use inline delegates

  updateProgress(uploadId, progress) {
    const progressElement = this.progressContainerTarget.querySelector(`[data-upload-id="${uploadId}"]`)
    if (progressElement) {
      const progressBar = progressElement.querySelector('.progress-bar')
      const progressText = progressElement.querySelector('.progress-text')
      
      if (progressBar) {
        progressBar.style.width = `${progress}%`
        progressBar.setAttribute('aria-valuenow', progress)
      }
      
      if (progressText) {
        progressText.textContent = `${Math.round(progress)}%`
      }
    }
  }

  handleUploadSuccess(uploadId, blob) {
    const uploadData = this.uploads.get(uploadId)
    if (!uploadData) return

    const { input, stemIndex } = uploadData
    this.completed.set(uploadId, blob)

    // Create hidden field with signed_blob_id
    this.createSignedBlobIdField(input, blob.signed_id, stemIndex)

    // Update progress to complete
    this.updateProgress(uploadId, 100)
    this.markProgressComplete(uploadId)

    this.dispatch('upload-success', { 
      detail: { 
        uploadId, 
        blob, 
        stemIndex,
        file: uploadData.file 
      } 
    })
  }

  handleUploadError(uploadId, error) {
    const uploadData = this.uploads.get(uploadId)
    if (!uploadData) return

    this.errors.set(uploadId, error)
    this.markProgressError(uploadId, error.message)

    this.dispatch('upload-error', { 
      detail: { 
        uploadId, 
        error, 
        stemIndex: uploadData.stemIndex,
        file: uploadData.file 
      } 
    })
  }

  createSignedBlobIdField(originalInput, signedId, stemIndex) {
    // Use base class method but keep this override for the display logic
    this.replaceFileInputWithSignedId(originalInput, signedId)
    
    // Add a visual indicator that file is uploaded
    this.createUploadSuccessIndicator(originalInput, signedId)
  }

  createUploadSuccessIndicator(originalInput, signedId) {
    const indicator = document.createElement('div')
    indicator.className = 'mt-2 p-3 bg-green-50 border border-green-200 rounded-md'
    indicator.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span class="text-sm font-medium text-green-800">Fichier uploadé avec succès</span>
        <button type="button" class="ml-auto text-green-600 hover:text-green-800" data-action="click->direct-upload#removeUpload" data-signed-id="${signedId}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `

    originalInput.parentNode.insertBefore(indicator, originalInput.nextSibling)
  }

  removeUpload(event) {
    const button = event.currentTarget
    const signedId = button.dataset.signedId
    
    // Find and remove the hidden input
    const hiddenInput = this.element.querySelector(`input[type="hidden"][value="${signedId}"]`)
    if (hiddenInput) {
      hiddenInput.remove()
    }

    // Remove the success indicator
    const indicator = button.closest('.bg-green-50')
    if (indicator) {
      // Show the original file input again
      const originalInput = indicator.parentNode.querySelector('input[type="file"]')
      if (originalInput) {
        originalInput.style.display = 'block'
      }
      indicator.remove()
    }

    this.checkSubmitButtonState()
  }

  createProgressElement(uploadId, fileName) {
    const progressElement = document.createElement('div')
    progressElement.className = 'mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg'
    progressElement.dataset.uploadId = uploadId

    progressElement.innerHTML = `
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-medium text-blue-800">${this.escapeHtml(fileName)}</span>
        <span class="text-sm text-blue-600 progress-text">0%</span>
      </div>
      <div class="w-full bg-blue-200 rounded-full h-2">
        <div class="bg-blue-600 h-2 rounded-full progress-bar transition-all duration-300" style="width: 0%" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>
      </div>
    `

    this.progressContainerTarget.appendChild(progressElement)
  }

  markProgressComplete(uploadId) {
    const progressElement = this.progressContainerTarget.querySelector(`[data-upload-id="${uploadId}"]`)
    if (progressElement) {
      progressElement.className = 'mb-4 p-4 bg-green-50 border border-green-200 rounded-lg'
      
      const progressText = progressElement.querySelector('.progress-text')
      if (progressText) {
        progressText.innerHTML = `
          <svg class="inline w-4 h-4 text-green-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Terminé
        `
        progressText.className = 'text-sm text-green-600 flex items-center'
      }
      
      const progressBar = progressElement.querySelector('.progress-bar')
      if (progressBar) {
        progressBar.className = 'bg-green-500 h-2 rounded-full progress-bar transition-all duration-300'
      }
    }
  }

  markProgressError(uploadId, errorMessage) {
    const progressElement = this.progressContainerTarget.querySelector(`[data-upload-id="${uploadId}"]`)
    if (progressElement) {
      progressElement.className = 'mb-4 p-4 bg-red-50 border border-red-200 rounded-lg'
      
      const progressText = progressElement.querySelector('.progress-text')
      if (progressText) {
        progressText.innerHTML = `
          <svg class="inline w-4 h-4 text-red-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          Erreur
        `
        progressText.className = 'text-sm text-red-600 flex items-center'
      }

      // Add error message
      const errorDiv = document.createElement('div')
      errorDiv.className = 'mt-2 text-sm text-red-600'
      errorDiv.textContent = errorMessage
      progressElement.appendChild(errorDiv)
    }
  }

  checkAllUploadsComplete() {
    const totalUploads = this.uploads.size
    const completedUploads = this.completed.size
    const errorUploads = this.errors.size

    if (completedUploads + errorUploads === totalUploads) {
      // All uploads are done (either completed or errored)
      if (errorUploads === 0) {
        // All successful
        this.enableSubmitButton()
        this.hideProgressContainerAfterDelay()
        
        // Auto-submit if requested
        if (this.autoSubmitOnComplete) {
          this.autoSubmitOnComplete = false
          const form = this.element.querySelector('form') || this.element.closest('form')
          if (form) {
            // Submit the form programmatically (bypasses our event listener)
            form.requestSubmit()
          }
        }
      } else {
        // Some errors
        this.showErrorSummary()
      }
    }
  }

  showProgressContainer() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.style.display = 'block'
    }
  }

  hideProgressContainerAfterDelay() {
    setTimeout(() => {
      if (this.hasProgressContainerTarget) {
        this.progressContainerTarget.style.display = 'none'
        this.progressContainerTarget.innerHTML = '' // Clear progress elements
      }
    }, 2000) // Hide after 2 seconds
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.originalSubmitText = this.submitButtonTarget.textContent
      this.submitButtonTarget.textContent = 'Upload en cours...'
    }
    // Also call base class method for other submit buttons
    this.disableSubmit()
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      if (this.originalSubmitText) {
        this.submitButtonTarget.textContent = this.originalSubmitText
      }
    }
    // Also call base class method for other submit buttons
    this.enableSubmit()
  }

  checkSubmitButtonState() {
    // Enable submit button only if no uploads are in progress
    const hasActiveUploads = this.uploads.size > this.completed.size + this.errors.size
    
    if (hasActiveUploads) {
      this.disableSubmitButton()
    } else {
      this.enableSubmitButton()
    }
  }

  showErrors(input, errors) {
    this.clearErrors(input)
    
    const errorContainer = document.createElement('div')
    errorContainer.className = 'mt-2 p-3 bg-red-50 border border-red-200 rounded-md upload-errors'
    errorContainer.innerHTML = `
      <div class="flex">
        <svg class="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div class="text-sm text-red-800">
          <ul class="list-disc list-inside space-y-1">
            ${errors.map(error => `<li>${this.escapeHtml(error)}</li>`).join('')}
          </ul>
        </div>
      </div>
    `

    input.parentNode.insertBefore(errorContainer, input.nextSibling)
  }

  clearErrors(input) {
    const existingErrors = input.parentNode.querySelectorAll('.upload-errors')
    existingErrors.forEach(error => error.remove())
  }

  showErrorSummary() {
    if (!this.hasErrorContainerTarget) return

    const errorCount = this.errors.size
    const totalCount = this.uploads.size

    this.errorContainerTarget.innerHTML = `
      <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div class="flex">
          <svg class="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div>
            <h3 class="text-sm font-medium text-red-800">
              Erreurs d'upload
            </h3>
            <p class="mt-1 text-sm text-red-600">
              ${errorCount} sur ${totalCount} fichiers n'ont pas pu être uploadés. Veuillez réessayer.
            </p>
          </div>
        </div>
      </div>
    `
    this.errorContainerTarget.style.display = 'block'
  }

  // Utility methods
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Public methods for external control
  resetUploads() {
    this.uploads.clear()
    this.completed.clear()
    this.errors.clear()
    
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.innerHTML = ''
      this.progressContainerTarget.style.display = 'none'
    }
    
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.style.display = 'none'
    }

    // Remove all direct upload fields
    this.element.querySelectorAll('input[data-direct-upload-field="true"]').forEach(input => {
      input.remove()
    })

    // Show all hidden file inputs
    this.element.querySelectorAll('input[type="file"][style*="display: none"]').forEach(input => {
      input.style.display = 'block'
    })

    // Remove success indicators
    this.element.querySelectorAll('.bg-green-50').forEach(indicator => {
      if (indicator.querySelector('svg')) {
        indicator.remove()
      }
    })

    this.checkSubmitButtonState()
  }
}